/**
 * Componente de preview universal para mídia configurada em nodes de fluxo
 *
 * Renderiza um card compacto com thumbnail/ícone por tipo (imagem, vídeo,
 * áudio, documento) e permite abrir um lightbox em tela cheia para visualizar
 * o conteúdo real do arquivo, seja a origem URL externa ou Galeria de Mídia.
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 */

import { useState } from 'react';
import { Eye, Play, Music, FileText, Image as ImageIcon, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { cn } from '@/lib/utils';

export type MediaConfigType = 'image' | 'video' | 'audio' | 'document';

interface MediaConfigPreviewProps {
  /** Tipo da mídia */
  mediaType: MediaConfigType;
  /** URL pública da mídia */
  mediaUrl: string;
  /** Nome amigável do arquivo (opcional) */
  fileName?: string;
  /** Classe extra opcional para o container */
  className?: string;
}

/**
 * Verifica se a URL parece válida o suficiente para tentar renderizar preview
 */
function isPreviewableUrl(url: string): boolean {
  if (!url || url.trim() === '') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function MediaConfigPreview({
  mediaType,
  mediaUrl,
  fileName,
  className,
}: MediaConfigPreviewProps) {
  const [open, setOpen] = useState(false);

  // Se URL inválida, não renderiza nada
  if (!isPreviewableUrl(mediaUrl)) return null;

  // Lightbox reaproveita ImageLightbox para image/video/document
  const lightboxType: 'image' | 'video' | 'document' | null =
    mediaType === 'image' ? 'image'
    : mediaType === 'video' ? 'video'
    : mediaType === 'document' ? 'document'
    : null;

  return (
    <div className={cn('rounded-lg border bg-muted/30 p-3', className)}>
      <div className="flex items-center gap-3">
        {/* Thumbnail por tipo */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative h-14 w-14 shrink-0 overflow-hidden rounded-md border bg-background flex items-center justify-center hover:opacity-90 transition"
          aria-label="Visualizar mídia"
        >
          {mediaType === 'image' && (
            <img
              src={mediaUrl}
              alt={fileName || 'Pré-visualização'}
              className="h-full w-full object-cover"
              onError={(e) => {
                // Fallback para ícone caso a imagem falhe ao carregar
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          {mediaType === 'video' && (
            <>
              <video src={mediaUrl} className="h-full w-full object-cover" preload="metadata" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Play className="h-5 w-5 text-white" />
              </div>
            </>
          )}
          {mediaType === 'audio' && <Music className="h-6 w-6 text-muted-foreground" />}
          {mediaType === 'document' && <FileText className="h-6 w-6 text-muted-foreground" />}
          {!['image', 'video', 'audio', 'document'].includes(mediaType) && (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </button>

        {/* Metadados */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">
            {fileName || 'Mídia configurada'}
          </p>
          <p className="text-xs text-muted-foreground capitalize">
            {mediaType === 'image' && 'Imagem'}
            {mediaType === 'video' && 'Vídeo'}
            {mediaType === 'audio' && 'Áudio'}
            {mediaType === 'document' && 'Documento'}
          </p>
          {/* Player inline para áudio: experiência rápida sem abrir lightbox */}
          {mediaType === 'audio' && (
            <audio src={mediaUrl} controls className="mt-2 h-8 w-full max-w-xs" />
          )}
        </div>

        {/* Botão Visualizar (sempre disponível) */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="gap-1.5"
        >
          <Eye className="h-4 w-4" />
          Visualizar
        </Button>
      </div>

      {/* Lightbox para image/video/document via componente existente */}
      {lightboxType && (
        <ImageLightbox
          open={open}
          onClose={() => setOpen(false)}
          mediaItems={[{ url: mediaUrl, type: lightboxType, title: fileName }]}
        />
      )}

      {/* Dialog dedicado para áudio (ImageLightbox não suporta) */}
      {mediaType === 'audio' && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md">
            <VisuallyHidden>
              <DialogTitle>{fileName || 'Áudio'}</DialogTitle>
            </VisuallyHidden>
            <div className="flex items-center gap-3 mb-3">
              <Music className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium truncate flex-1">
                {fileName || 'Áudio configurado'}
              </p>
            </div>
            <audio src={mediaUrl} controls autoPlay className="w-full" />
            <div className="mt-4 flex justify-end gap-2">
              <Button asChild variant="outline" size="sm">
                <a href={mediaUrl} download target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4 mr-1.5" />
                  Baixar
                </a>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                <X className="h-4 w-4 mr-1.5" />
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
